import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useI18n } from '../i18n/index.jsx'
import ReactGA from 'react-ga4'
import { createClient } from '@supabase/supabase-js'
import { GlobalCookieConsent } from '../components/GlobalCookieConsent'

// Initialize Supabase with environment variables
// ✅ Using VITE_* variables (safe for frontend)
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseKey) {
  console.error('❌ Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY in environment variables')
}

const supabase = createClient(supabaseUrl || '', supabaseKey || '')

export default function Login() {
  const { t } = useI18n()
  const [formData, setFormData] = useState({ email: '', password: '' })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const navigate = useNavigate()

  // Track page view
  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: "/login", title: "Login" });
  }, []);

  const onChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value })
  
  const handleGoogleLogin = async () => {
    setLoading(true)
    setError('')
    
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      })
      
      if (error) {
        setError(error.message)
      }
    } catch (err) {
      setError(err.message || 'Erreur de connexion avec Google')
    } finally {
      setLoading(false)
    }
  }

  const handleGithubLogin = async () => {
    setLoading(true)
    setError('')
    
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'github',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      })
      
      if (error) {
        setError(error.message)
      }
    } catch (err) {
      setError(err.message || 'Erreur de connexion avec GitHub')
    } finally {
      setLoading(false)
    }
  }
  
  const onSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
  const response = await fetch('https://lrv7zfe6dc7hrpucp3qulxlzaq0fszlw.lambda-url.eu-west-3.on.aws/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        })
      })

      const data = await response.json()

      if (response.ok) {
        // Stocker le token JWT
        localStorage.setItem('access_token', data.access_token)
        localStorage.setItem('token_type', (data.token_type && /bearer/i.test(data.token_type)) ? 'Bearer' : (data.token_type || 'Bearer'))
        localStorage.setItem('email', formData.email)
        
        // Stocker l'ID utilisateur s'il est fourni
        if (data.user_id) {
          localStorage.setItem('user_id', data.user_id)
        }
        
        // Rediriger vers le dashboard
        navigate('/dashboard')
      } else {
        setError(data.detail || t('auth.error.invalid'))
      }
    } catch (err) {
      setError(t('auth.error.server'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-950 via-black to-slate-950 text-slate-100">
      {/* Bouton retour page d'accueil */}
      <button
        type="button"
        onClick={() => navigate('/')}
        className="absolute top-7 left-7 z-30 flex items-center gap-3 px-6 py-3 rounded-2xl bg-white/10 text-slate-100 text-base font-semibold border border-white/20 shadow-xl backdrop-blur-md hover:bg-white/20 hover:text-emerald-400 transition-all duration-150"
        style={{boxShadow:'0 8px 32px rgba(16,185,129,0.12)', backdropFilter:'blur(12px) saturate(120%)'}}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="inline-block align-middle"><polyline points="15 18 9 12 15 6"></polyline></svg>
        {t('backHome') || 'Accueil'}
      </button>
      {/* Glows décoratifs */}
      <div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-emerald-500/25 blur-3xl hidden md:block" />
      <div className="pointer-events-none absolute bottom-[-90px] right-[-90px] h-96 w-96 rounded-full bg-sky-500/20 blur-3xl hidden md:block" />

      <div className="relative z-10 flex min-h-screen items-center justify-center p-4">
        {/* Carte en verre */}
        <div className="w-full max-w-md rounded-2xl border border-white/10 bg-white/5 p-8 shadow-[0_0_1px_rgba(255,255,255,0.25),0_25px_80px_-20px_rgba(16,185,129,0.35)] backdrop-blur-2xl">
          {/* Header */}
          <div className="mb-8 text-center">
            <div className="mx-auto mb-4 h-14 w-14 flex items-center justify-center rounded-full bg-white/10 ring-1 ring-inset ring-blue-400/30">
              <img src="/logo_svg.svg" alt="Logo" className="h-12 w-12" />
            </div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('auth.login.title')}</h1>
            <p className="mt-1 text-sm text-slate-400">{t('auth.login.subtitle')}</p>
          </div>

          {/* Message d'erreur */}
          {error && (
            <div className="mb-6 rounded-lg bg-red-500/20 border border-red-500/30 p-3 text-center text-sm text-red-300">
              {error}
            </div>
          )}

          {/* Formulaire */}
          <form onSubmit={onSubmit} className="space-y-4">
            {/* Email */}
            <div className="space-y-1">
              <label htmlFor="email" className="text-sm text-slate-300">{t('auth.email')}</label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5"><path d="M1.5 8.67v8.58A2.25 2.25 0 0 0 3.75 19.5h16.5a2.25 2.25 0 0 0 2.25-2.25V8.67l-8.724 4.362a3.75 3.75 0 0 1-3.252 0L1.5 8.67Z"/><path d="M22.5 6.908V6.75A2.25 2.25 0 0 0 20.25 4.5H3.75A2.25 2.25 0 0 0 1.5 6.75v.158l9.276 4.64a2.25 2.25 0 0 0 1.948 0L22.5 6.908Z"/></svg>
                </span>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={onChange}
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-10 py-3 text-slate-100 placeholder:text-slate-500 outline-none ring-0 transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/40"
                  placeholder="email@example.com"
                  required
                />
              </div>
            </div>

            {/* Mot de passe */}
            <div className="space-y-1">
              <label htmlFor="password" className="text-sm text-slate-300">{t('auth.password')}</label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5"><path fillRule="evenodd" d="M12 1.5a5.25 5.25 0 0 0-5.25 5.25V9A3.75 3.75 0 0 0 3 12.75v4.5A3.75 3.75 0 0 0 6.75 21h10.5A3.75 3.75 0 0 0 21 17.25v-4.5A3.75 3.75 0 0 0 17.25 9V6.75A5.25 5.25 0 0 0 12 1.5Zm-1.125 11.625a1.125 1.125 0 1 1 2.25 0v2.25a1.125 1.125 0 1 1-2.25 0v-2.25Z" clipRule="evenodd"/></svg>
                </span>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={onChange}
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-10 py-3 text-slate-100 placeholder:text-slate-500 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/40"
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>

            {/* Forgot password */}
            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-sm text-emerald-400 hover:text-emerald-300">{t('auth.forgot')}</Link>
            </div>

            {/* CTA */}
            <button
              type="submit"
              disabled={loading}
              className="group mt-6 w-full transform rounded-lg bg-emerald-500 py-3 font-semibold text-slate-950 transition hover:-translate-y-0.5 hover:bg-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/60 focus:ring-offset-0 disabled:cursor-not-allowed disabled:bg-slate-600 disabled:text-slate-300 disabled:transform-none"
            >
              {loading ? t('auth.login.submitting') : t('auth.login.submit')}
              {!loading && <span className="ml-1 inline-block transition-transform group-hover:translate-x-0.5">→</span>}
            </button>
          </form>

          {/* Séparateur */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-slate-950/90 px-3 text-slate-400">{t('auth.or') || 'ou'}</span>
            </div>
          </div>

          {/* Boutons OAuth */}
          <div className="flex gap-3">
            {/* Bouton Google */}
            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/5 py-3 font-medium text-slate-100 transition hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span className="hidden sm:inline">Google</span>
            </button>

            {/* Bouton GitHub */}
            <button
              type="button"
              onClick={handleGithubLogin}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/5 py-3 font-medium text-slate-100 transition hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v 3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
              </svg>
              <span className="hidden sm:inline">GitHub</span>
            </button>
          </div>

          <p className="mt-6 text-center text-sm text-slate-400">
            {t('auth.login.noAccount')}{' '}
            <Link to="/signup" className="text-emerald-400 hover:text-emerald-300">{t('auth.signup.link')}</Link>
          </p>
        </div>
      </div>

      {/* Cookie Consent */}
      <GlobalCookieConsent />
    </div>
  )
}
